import React, { useCallback, useEffect, useId, useRef, useState } from "react";

import {
  INITIAL_ZOOM,
  MAX_ZOOM,
  MIN_ZOOM,
  clampOffset,
  createInitialCropState,
  computeEffectiveScale,
  nudgeAmountForKeyboard,
  nudgeCrop,
  withRotation,
  withZoom,
} from "./avatarCropModel";
import {
  CROP_DECODE_ERROR_MESSAGE,
  CROP_DIMENSION_ERROR_MESSAGE,
  CROP_EXPORT_ERROR_MESSAGE,
} from "./avatarCropEligibility";
import {
  AvatarCropError,
  createCroppedAvatarFile,
} from "./createCroppedAvatarFile";

const DEFAULT_VIEWPORT = 320;

/**
 * Inline 1:1 avatar crop editor. Dependency-free; Pointer Events + Canvas.
 *
 * Props:
 * - sourceFile: File to crop (required while open)
 * - title: Ukrainian heading
 * - onApply(file): cropped JPEG File
 * - onCancel(): discard uncommitted source
 * - disabled: parent-forced disable
 */
function AvatarCropEditor({
  sourceFile,
  title = "Обрізати зображення",
  onApply,
  onCancel,
  disabled = false,
}) {
  const titleId = useId();
  const instructionsId = useId();
  const zoomLabelId = useId();
  const viewportRef = useRef(null);
  const objectUrlRef = useRef(null);
  const activePointerIdRef = useRef(null);
  const dragOriginRef = useRef(null);
  const mountedRef = useRef(true);
  const applyRunIdRef = useRef(0);
  const sourceFileRef = useRef(sourceFile);
  sourceFileRef.current = sourceFile;

  const [objectUrl, setObjectUrl] = useState(null);
  const [naturalSize, setNaturalSize] = useState(null);
  const [viewportSize, setViewportSize] = useState(DEFAULT_VIEWPORT);
  const [cropState, setCropState] = useState(null);
  const [decodeError, setDecodeError] = useState(null);
  const [exportError, setExportError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const invalidateApplyRun = useCallback(() => {
    applyRunIdRef.current += 1;
  }, []);

  const revokeCurrentUrl = useCallback(() => {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
  }, []);

  // Mounted / active-editor identity: invalidate before any stale result commits.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      invalidateApplyRun();
    };
  }, [invalidateApplyRun]);

  // Measure viewport (square, responsive).
  useEffect(() => {
    const el = viewportRef.current;
    if (!el || typeof ResizeObserver === "undefined") {
      return undefined;
    }
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const side = Math.floor(entry.contentRect.width);
      if (side > 0) setViewportSize(side);
    });
    ro.observe(el);
    const initial = Math.floor(el.getBoundingClientRect().width);
    if (initial > 0) setViewportSize(initial);
    return () => ro.disconnect();
  }, [objectUrl]);

  // Source object URL. Natural dimensions come from the preview <img> onLoad
  // so preview and export share the same browser-decoded orientation path.
  // Source replacement invalidates any in-flight Apply for the prior File.
  useEffect(() => {
    invalidateApplyRun();
    setNaturalSize(null);
    setCropState(null);
    setDecodeError(null);
    setExportError(null);
    setProcessing(false);

    if (!sourceFile) {
      revokeCurrentUrl();
      setObjectUrl(null);
      return undefined;
    }

    const url = URL.createObjectURL(sourceFile);
    objectUrlRef.current = url;
    setObjectUrl(url);

    return () => {
      URL.revokeObjectURL(url);
      if (objectUrlRef.current === url) {
        objectUrlRef.current = null;
      }
    };
  }, [sourceFile, revokeCurrentUrl, invalidateApplyRun]);

  const handlePreviewLoad = (event) => {
    const img = event.currentTarget;
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!(w > 0) || !(h > 0)) {
      setDecodeError(CROP_DIMENSION_ERROR_MESSAGE);
      setNaturalSize(null);
      return;
    }
    setDecodeError(null);
    setNaturalSize({ width: w, height: h });
  };

  const handlePreviewError = () => {
    setDecodeError(CROP_DECODE_ERROR_MESSAGE);
    setNaturalSize(null);
  };

  // Initialize / resize crop state when natural size or viewport changes.
  useEffect(() => {
    if (!naturalSize || !(viewportSize > 0)) return;
    setCropState((prev) => {
      const sameNatural =
        prev &&
        (prev.naturalWidth || prev.sourceWidth) === naturalSize.width &&
        (prev.naturalHeight || prev.sourceHeight) === naturalSize.height;
      if (sameNatural) {
        const next = {
          ...prev,
          viewportSize,
        };
        const clamped = clampOffset(
          next.offsetX,
          next.offsetY,
          next.sourceWidth,
          next.sourceHeight,
          viewportSize,
          next.zoom,
        );
        return { ...next, ...clamped };
      }
      return createInitialCropState(
        naturalSize.width,
        naturalSize.height,
        viewportSize,
      );
    });
  }, [naturalSize, viewportSize]);

  const busy = processing || disabled;
  const surfaceError = decodeError || exportError;

  const cropStateRef = useRef(cropState);
  const busyRef = useRef(busy);
  const decodeErrorRef = useRef(decodeError);
  cropStateRef.current = cropState;
  busyRef.current = busy;
  decodeErrorRef.current = decodeError;

  // Native Pointer Events on the viewport (pointer capture + jsdom-friendly).
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;

    const onPointerDown = (event) => {
      if (busyRef.current || !cropStateRef.current || decodeErrorRef.current) {
        return;
      }
      if (activePointerIdRef.current != null) return;
      event.preventDefault();
      el.setPointerCapture?.(event.pointerId);
      activePointerIdRef.current = event.pointerId;
      const state = cropStateRef.current;
      dragOriginRef.current = {
        x: event.clientX,
        y: event.clientY,
        offsetX: state.offsetX,
        offsetY: state.offsetY,
      };
    };

    const onPointerMove = (event) => {
      if (activePointerIdRef.current !== event.pointerId) return;
      const origin = dragOriginRef.current;
      const state = cropStateRef.current;
      if (!origin || !state) return;
      const dx = event.clientX - origin.x;
      const dy = event.clientY - origin.y;
      const clamped = clampOffset(
        origin.offsetX + dx,
        origin.offsetY + dy,
        state.sourceWidth,
        state.sourceHeight,
        state.viewportSize,
        state.zoom,
      );
      setCropState((prev) => (prev ? { ...prev, ...clamped } : prev));
    };

    const endPointerDrag = (event) => {
      if (activePointerIdRef.current !== event.pointerId) return;
      try {
        el.releasePointerCapture?.(event.pointerId);
      } catch (_err) {
        // ignore release errors when capture was never set
      }
      activePointerIdRef.current = null;
      dragOriginRef.current = null;
    };

    el.addEventListener("pointerdown", onPointerDown);
    el.addEventListener("pointermove", onPointerMove);
    el.addEventListener("pointerup", endPointerDrag);
    el.addEventListener("pointercancel", endPointerDrag);

    return () => {
      el.removeEventListener("pointerdown", onPointerDown);
      el.removeEventListener("pointermove", onPointerMove);
      el.removeEventListener("pointerup", endPointerDrag);
      el.removeEventListener("pointercancel", endPointerDrag);
      activePointerIdRef.current = null;
      dragOriginRef.current = null;
    };
  }, [objectUrl]);

  const handleKeyDown = (event) => {
    if (busy || !cropState || decodeError) return;
    const map = {
      ArrowLeft: [-1, 0],
      ArrowRight: [1, 0],
      ArrowUp: [0, -1],
      ArrowDown: [0, 1],
    };
    const delta = map[event.key];
    if (!delta) return;
    event.preventDefault();
    const amount = nudgeAmountForKeyboard(event.shiftKey);
    setCropState((prev) =>
      nudgeCrop(prev, delta[0] * amount, delta[1] * amount),
    );
  };

  const handleZoomChange = (event) => {
    if (busy || !cropState) return;
    const next = Number(event.target.value);
    setCropState((prev) => withZoom(prev, next));
  };

  const handleReset = () => {
    if (busy || !naturalSize) return;
    setExportError(null);
    setCropState(
      createInitialCropState(
        naturalSize.width,
        naturalSize.height,
        viewportSize,
      ),
    );
  };

  const handleRotate = (delta) => {
    if (busy || !cropState || decodeError) return;
    setExportError(null);
    setCropState((prev) => withRotation(prev, delta));
  };

  const canCommitApplyRun = (runId, sourceFileAtStart) =>
    mountedRef.current &&
    applyRunIdRef.current === runId &&
    sourceFileRef.current === sourceFileAtStart;

  const handleCancel = () => {
    // Invalidate in-flight Apply before owner cancellation (modal close path).
    invalidateApplyRun();
    setProcessing(false);
    setExportError(null);
    onCancel?.();
  };

  const handleApply = async () => {
    if (busy || !cropState || !sourceFile || decodeError) return;
    const runId = ++applyRunIdRef.current;
    const sourceFileAtStart = sourceFile;
    setProcessing(true);
    setExportError(null);
    try {
      const cropped = await createCroppedAvatarFile(
        sourceFileAtStart,
        cropState,
        cropState.viewportSize,
      );
      if (!canCommitApplyRun(runId, sourceFileAtStart)) {
        return;
      }
      onApply?.(cropped);
    } catch (err) {
      if (!canCommitApplyRun(runId, sourceFileAtStart)) {
        return;
      }
      const message =
        err instanceof AvatarCropError
          ? err.userMessage
          : CROP_EXPORT_ERROR_MESSAGE;
      setExportError(message);
    } finally {
      // Stale finally must not clear processing for a newer Apply run.
      if (
        mountedRef.current &&
        applyRunIdRef.current === runId
      ) {
        setProcessing(false);
      }
    }
  };

  const effectiveScale =
    cropState &&
    computeEffectiveScale(
      cropState.sourceWidth,
      cropState.sourceHeight,
      cropState.viewportSize,
      cropState.zoom,
    );

  const rotation = cropState?.rotation || 0;

  // Preview sizes the raw decoded image, then CSS-rotates. Crop geometry uses
  // post-rotation sourceWidth/sourceHeight (swapped on ±90°).
  const imageStyle =
    cropState && effectiveScale && naturalSize
      ? {
          width: `${naturalSize.width * effectiveScale}px`,
          height: `${naturalSize.height * effectiveScale}px`,
          transform: `translate(-50%, -50%) translate(${cropState.offsetX}px, ${cropState.offsetY}px) rotate(${rotation}deg)`,
        }
      : {
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transform: "translate(-50%, -50%)",
        };

  return (
    <div
      className="avatar-crop-editor"
      data-avatar-crop="editor"
      role="region"
      aria-labelledby={titleId}
    >
      <h3 id={titleId} className="avatar-crop-editor__title">
        {title}
      </h3>

      <div
        ref={viewportRef}
        className="avatar-crop-editor__viewport"
        role="img"
        tabIndex={0}
        aria-label="Область обрізання зображення"
        aria-describedby={instructionsId}
        data-avatar-crop="viewport"
        onKeyDown={handleKeyDown}
      >
        {objectUrl && (
          <img
            src={objectUrl}
            alt=""
            draggable={false}
            className="avatar-crop-editor__image"
            style={imageStyle}
            data-avatar-crop="image"
            onLoad={handlePreviewLoad}
            onError={handlePreviewError}
          />
        )}
        <div
          className="avatar-crop-editor__guide"
          aria-hidden="true"
          data-avatar-crop="guide"
          data-avatar-crop-guide="circular"
          data-avatar-crop-exterior="dimmed"
        />
      </div>

      <p id={instructionsId} className="avatar-crop-editor__instructions">
        Перетягніть зображення, щоб змінити кадр. Клавіші зі стрілками зсувають
        зображення; Shift + стрілка — більший крок. Кнопки обертання повертають
        на 90°.
      </p>

      <div className="avatar-crop-editor__rotate" role="group" aria-label="Обертання">
        <button
          type="button"
          className="avatar-crop-editor__btn avatar-crop-editor__btn--secondary"
          onClick={() => handleRotate(-90)}
          disabled={busy || !cropState || Boolean(decodeError)}
          aria-label="Обернути ліворуч на 90 градусів"
          data-avatar-crop="rotate-left"
        >
          Обернути ліворуч
        </button>
        <button
          type="button"
          className="avatar-crop-editor__btn avatar-crop-editor__btn--secondary"
          onClick={() => handleRotate(90)}
          disabled={busy || !cropState || Boolean(decodeError)}
          aria-label="Обернути праворуч на 90 градусів"
          data-avatar-crop="rotate-right"
        >
          Обернути праворуч
        </button>
      </div>

      <div className="avatar-crop-editor__zoom">
        <label id={zoomLabelId} htmlFor={`${zoomLabelId}-range`}>
          Масштаб
        </label>
        <input
          id={`${zoomLabelId}-range`}
          type="range"
          min={MIN_ZOOM}
          max={MAX_ZOOM}
          step={0.01}
          value={cropState?.zoom ?? INITIAL_ZOOM}
          onChange={handleZoomChange}
          disabled={busy || !cropState || Boolean(decodeError)}
          aria-labelledby={zoomLabelId}
          data-avatar-crop="zoom"
        />
      </div>

      {surfaceError && (
        <p
          className="avatar-crop-editor__error"
          role="alert"
          data-avatar-crop="error"
        >
          {surfaceError}
        </p>
      )}

      {processing && (
        <p
          className="avatar-crop-editor__status"
          role="status"
          aria-live="polite"
          data-avatar-crop="processing"
        >
          Створення обрізаного зображення…
        </p>
      )}

      <div className="avatar-crop-editor__actions">
        <button
          type="button"
          className="avatar-crop-editor__btn avatar-crop-editor__btn--secondary"
          onClick={handleReset}
          disabled={busy || !cropState || Boolean(decodeError)}
          data-avatar-crop="reset"
        >
          Скинути
        </button>
        <button
          type="button"
          className="avatar-crop-editor__btn avatar-crop-editor__btn--secondary"
          onClick={handleCancel}
          disabled={disabled}
          data-avatar-crop="cancel"
        >
          Скасувати
        </button>
        <button
          type="button"
          className="avatar-crop-editor__btn avatar-crop-editor__btn--primary"
          onClick={handleApply}
          disabled={busy || !cropState || Boolean(decodeError)}
          data-avatar-crop="apply"
        >
          Застосувати
        </button>
      </div>
    </div>
  );
}

export default AvatarCropEditor;
